import * as React from "react";
import { format, parse, isValid } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * DateInput — iOS-friendly native date picker wrapper.
 *
 * Uses the browser's native <input type="date"> which is reliable on iOS Safari,
 * iOS Chrome, Android Chrome and all modern desktop browsers. The native iOS
 * picker is best-in-class (wheel/scroller) and avoids the Radix Popover + Calendar
 * tap/scroll issues that plague mobile webviews.
 *
 * Accepts/returns a JS Date object (or null) so it's a drop-in replacement for
 * the previous Popover+Calendar usage.
 */
export const DateInput = React.forwardRef(
  (
    {
      value,
      onChange,
      placeholder = "Select date",
      className,
      disabled = false,
      max,
      min,
      "data-testid": dataTestId,
      id,
      ...rest
    },
    ref
  ) => {
    const toIso = (d) => (d instanceof Date && isValid(d) ? format(d, "yyyy-MM-dd") : "");
    const inputValue = toIso(value);

    const handleChange = (e) => {
      const v = e.target.value;
      if (!v) {
        onChange?.(null);
        return;
      }
      const parsed = parse(v, "yyyy-MM-dd", new Date());
      onChange?.(isValid(parsed) ? parsed : null);
    };

    const maxIso = max instanceof Date ? toIso(max) : max;
    const minIso = min instanceof Date ? toIso(min) : min;

    return (
      <div className={cn("relative w-full", className)}>
        <input
          ref={ref}
          id={id}
          type="date"
          value={inputValue}
          onChange={handleChange}
          disabled={disabled}
          max={maxIso}
          min={minIso}
          placeholder={placeholder}
          data-testid={dataTestId}
          className={cn(
            "flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background",
            "appearance-none",
            "placeholder:text-muted-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            // iOS Safari: ensure tap area is large enough and the picker opens reliably
            "min-h-[44px] cursor-pointer",
            !inputValue && "text-muted-foreground",
            "pr-10"
          )}
          {...rest}
        />
        <CalendarIcon
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
      </div>
    );
  }
);

DateInput.displayName = "DateInput";

export default DateInput;
